import React, { useState, useEffect } from 'react';
import { 
  FaDatabase, 
  FaPlus, 
  FaTrash, 
  FaEdit, 
  FaSave, 
  FaTimes,
  FaCheck,
  FaList,
  FaUserTag,
  FaLanguage,
  FaBriefcase,
  FaSearch,
  FaShieldAlt,
  FaArchive,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSitemap // Added for subcastes icon
} from 'react-icons/fa';
import { fetchMasterData, updateMasterCategory, updateCastesForReligion,     
    updateSubcastesForCaste } from '../../services/masterDataApi';
    import CustomDropdown from "./CustomDropdown";

const MasterDataManagement = () => {
    const [activeTab, setActiveTab] = useState('religions');
    const [masterData, setMasterData] = useState({});
    const [newItem, setNewItem] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [editingItem, setEditingItem] = useState({ category: null, index: null, value: '' });

    // For castes & subcastes: selected parent (religion for castes, caste for subcastes)
    const [selectedParent, setSelectedParent] = useState('');

    // Modal state
    const [modal, setModal] = useState({
        show: false,
        type: 'success', // success, error, confirm
        title: '',
        message: '',
        onConfirm: null // only used for confirm type
    });

    const openModal = (type, title, message, onConfirm = null) => {
        setModal({
            show: true,
            type,
            title,
            message,
            onConfirm
        });
    };

    const closeModal = () => {
        setModal({
            show: false,
            type: 'success',
            title: '',
            message: '',
            onConfirm: null
        });
    };

    const getTabIcon = (tab) => {
        switch(tab) {
            case 'religions': return <FaShieldAlt />;
            case 'castes': return <FaUserTag />;
            case 'subcastes': return <FaSitemap />;
            case 'occupations': return <FaBriefcase />;
            case 'education': return <FaArchive />;
            default: return <FaDatabase />;
        }
    };

    // Options for Religion dropdown (Castes tab)
const religionOptions = (masterData.religions || []).map(rel => ({
  value: rel,
  label: rel
}));

// Options for Caste dropdown (Subcastes tab)
// We use Object.keys(masterData.castes) to get all caste names
const casteOptions = Object.keys(masterData.castes || {}).map(caste => ({
  value: caste,
  label: caste
}));

    useEffect(() => {
        loadData();
    }, []);

const loadData = async () => {
    setLoading(true);
    try {
        const res = await fetchMasterData();
        console.log('Raw API response:', res); // Debug: check structure

        // Safely extract the actual master data
        const masterDataToUse = res?.data || res || {
            religions: [],
            castes: {},
            subcastes: {},
            education: [],
            occupations: []
        };

        setMasterData(masterDataToUse);
        setLoading(false);
    } catch (err) {
        console.error('Failed to fetch master data:', err);
        openModal('error', 'Error', 'Failed to load master data. Please try again.');
        setLoading(false);
    }
};

    // Get current items to display (handles arrays and nested Maps)
    const getCurrentItems = () => {
        if (activeTab === 'castes') {
            return selectedParent ? (masterData.castes?.[selectedParent] || []) : [];
        }
        if (activeTab === 'subcastes') {
            return selectedParent ? (masterData.subcastes?.[selectedParent] || []) : [];
        }
        return masterData[activeTab] || [];
    };

    const currentItems = getCurrentItems();
    const filteredData = Array.isArray(currentItems)
        ? currentItems.filter(item => item.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    const handleAdd = async () => {
        if (!newItem.trim()) return;

        const trimmed = newItem.trim();
        if (currentItems.includes(trimmed)) {
            openModal('error', 'Duplicate', 'This item already exists');
            return;
        }

        const updated = [...currentItems, trimmed];

        try {
            if (activeTab === 'castes') {
                await updateCastesForReligion(selectedParent, updated);
                openModal('success', 'Success', `${trimmed} added to ${selectedParent}`);
            } else if (activeTab === 'subcastes') {
                await updateSubcastesForCaste(selectedParent, updated);
                openModal('success', 'Success', `${trimmed} added under ${selectedParent}`);
            } else {
                await updateMasterCategory(activeTab, updated);
                openModal('success', 'Success', 'Added successfully');
            }

            // Update local state after success
            if (activeTab === 'castes' || activeTab === 'subcastes') {
                const field = activeTab === 'castes' ? 'castes' : 'subcastes';
                setMasterData(prev => ({
                    ...prev,
                    [field]: {
                        ...(prev[field] || {}),
                        [selectedParent]: updated
                    }
                }));
            } else {
                setMasterData(prev => ({ ...prev, [activeTab]: updated }));
            }
            setNewItem('');
        } catch (err) {
            console.error(err);
            openModal('error', 'Error', 'Failed to save. Please try again.');
        }
    };

    const handleDelete = async (item) => {
        const updated = currentItems.filter(i => i !== item);

        try {
            if (activeTab === 'castes') {
                await updateCastesForReligion(selectedParent, updated);
            } else if (activeTab === 'subcastes') {
                await updateSubcastesForCaste(selectedParent, updated);
            } else {
                await updateMasterCategory(activeTab, updated);
            }

            if (activeTab === 'castes' || activeTab === 'subcastes') {
                const field = activeTab === 'castes' ? 'castes' : 'subcastes';
                setMasterData(prev => ({
                    ...prev,
                    [field]: {
                        ...(prev[field] || {}),
                        [selectedParent]: updated
                    }
                }));
            } else {
                setMasterData(prev => ({
                    ...prev,
                    [activeTab]: updated
                }));
            }

            openModal('success', 'Deleted', 'Removed successfully');
        } catch (err) {
            openModal('error', 'Error', 'Failed to delete');
        }
    };

    const startEditing = (item, index) => {
        setEditingItem({ 
            category: activeTab, 
            index, 
            value: item,
            parent: selectedParent // remember parent for nested fields
        });
    };

    const saveEdit = async () => {
        if (!editingItem.value.trim()) {
            openModal('error', 'Invalid Input', 'Cannot save empty value');
            return;
        }

        const updatedItems = [...currentItems];
        updatedItems[editingItem.index] = editingItem.value.trim();

        try {
            if (editingItem.category === 'castes') {
                await updateCastesForReligion(editingItem.parent, updatedItems);
            } else if (editingItem.category === 'subcastes') {
                await updateSubcastesForCaste(editingItem.parent, updatedItems);
            } else {
                await updateMasterCategory(editingItem.category, updatedItems);
            }

            if (editingItem.category === 'castes' || editingItem.category === 'subcastes') {
                const field = editingItem.category === 'castes' ? 'castes' : 'subcastes';
                setMasterData(prev => ({
                    ...prev,
                    [field]: {
                        ...(prev[field] || {}),
                        [editingItem.parent]: updatedItems
                    }
                }));
            } else {
                setMasterData(prev => ({
                    ...prev,
                    [editingItem.category]: updatedItems
                }));
            }

            openModal('success', 'Updated', 'Item updated successfully');
            setEditingItem({ category: null, index: null, value: '', parent: '' });
        } catch (err) {
            openModal('error', 'Error', 'Failed to update');
        }
    };

    // Total items count
    const totalItems = 
        (masterData.religions?.length || 0) +
        (masterData.occupations?.length || 0) +
        (masterData.education?.length || 0) +
        Object.values(masterData.castes || {}).flat().length +
        Object.values(masterData.subcastes || {}).flat().length;

    return (
        <div className="space-y-8">
            {/* Modal - Your original modal, unchanged */}
            {modal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-scale-in">
                        <div className="flex flex-col items-center text-center">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                                modal.type === 'success' ? 'bg-green-100 text-green-600' :
                                modal.type === 'error' ? 'bg-red-100 text-red-600' :
                                'bg-yellow-100 text-yellow-600'
                            }`}>
                                {modal.type === 'success' && <FaCheck className="text-4xl" />}
                                {modal.type === 'error' && <FaExclamationTriangle className="text-4xl" />}
                                {modal.type === 'confirm' && <FaInfoCircle className="text-4xl" />}
                            </div>

                            <h3 className="text-2xl font-bold text-gray-800 mb-3">{modal.title}</h3>
                            <p className="text-gray-600 mb-8">{modal.message}</p>

                            <div className="flex gap-4 w-full">
                                {modal.type === 'confirm' ? (
                                    <>
                                        <button
                                            onClick={() => {
                                                if (modal.onConfirm) modal.onConfirm();
                                            }}
                                            className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors"
                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={closeModal}
                                        className="w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
                                    >
                                        OK
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                            <FaDatabase className="mr-3 text-blue-500" />
                            Master Data Management
                        </h1>
                        <p className="text-gray-600 mt-1">Manage platform constants and reference data</p>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="px-4 py-2.5 bg-blue-500 text-white rounded-xl font-medium">
                            {totalItems} Total Items
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden min-h-[600px] flex flex-col md:flex-row">
                {/* Sidebar Navigation */}
                <div className="w-full md:w-72 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Categories</h3>
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search categories..."
                                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    
                    <nav className="p-4 space-y-1">
                        {['religions', 'castes', 'subcastes', 'occupations', 'education'].map(key => (
                            <button
                                key={key}
                                onClick={() => {
                                    setActiveTab(key);
                                    setSelectedParent('');
                                    setSearchTerm('');
                                }}
                                className={`w-full flex items-center px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 capitalize ${
                                    activeTab === key 
                                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30' 
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                <span className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                                    activeTab === key ? 'bg-white/20' : 'bg-gray-100 text-gray-500'
                                }`}>
                                    {getTabIcon(key)}
                                </span>
                                <span className="flex-1 text-left">{key}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    activeTab === key ? 'bg-white/20' : 'bg-gray-200 text-gray-600'
                                }`}>
                                    {key === 'castes' ? Object.values(masterData.castes || {}).flat().length :
                                     key === 'subcastes' ? Object.values(masterData.subcastes || {}).flat().length :
                                     masterData[key]?.length || 0}
                                </span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col">
                    {/* Content Header */}
                    <div className="p-6 border-b border-gray-200 bg-white">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div className="flex items-center">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${
                                    activeTab === 'religions' ? 'bg-blue-100 text-blue-600' :
                                    activeTab === 'castes' ? 'bg-green-100 text-green-600' :
                                    activeTab === 'subcastes' ? 'bg-purple-100 text-purple-600' :
                                    activeTab === 'occupations' ? 'bg-orange-100 text-orange-600' :
                                    activeTab === 'education' ? 'bg-red-100 text-red-600' :
                                    'bg-indigo-100 text-indigo-600'
                                }`}>
                                    {getTabIcon(activeTab)}
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800 capitalize">{activeTab}</h2>
                                    <p className="text-gray-600 text-sm">
                                        {activeTab === 'castes' ? 'Manage castes under each religion' :
                                         activeTab === 'subcastes' ? 'Manage subcastes under each caste' :
                                         `Manage ${activeTab} data entries`}
                                    </p>
                                </div>
                            </div>
                            
                            {/* FIXED: Parent Selector for castes/subcastes */}
{(activeTab === 'castes' || activeTab === 'subcastes') && (
    <div className="w-64">
        <CustomDropdown
            options={activeTab === 'castes' ? religionOptions : casteOptions}
            value={selectedParent}
            onChange={(val) => setSelectedParent(val)}
            placeholder={`Select ${activeTab === 'castes' ? 'Religion' : 'Caste'}`}
            searchable={true}
        />
    </div>
)}

                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={`Search ${activeTab}...`}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Add New Item */}
                    <div className="p-6 border-b border-gray-200 bg-gray-50">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <FaPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={newItem}
                                        onChange={(e) => setNewItem(e.target.value)}
                                        placeholder={
                                            (activeTab === 'castes' || activeTab === 'subcastes') && !selectedParent
                                                ? `Select ${activeTab === 'castes' ? 'religion' : 'caste'} first`
                                                : `Add new ${activeTab.slice(0, -1)}...`
                                        }
                                        disabled={(activeTab === 'castes' || activeTab === 'subcastes') && !selectedParent}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                        onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
                                    />
                                </div>
                            </div>
                            <button
                                onClick={handleAdd}
                                disabled={(activeTab === 'castes' || activeTab === 'subcastes') && !selectedParent}
                                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center justify-center disabled:opacity-50"
                            >
                                <FaPlus className="mr-2" /> Add New
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mt-3">Press Enter or click Add New to save</p>
                    </div>

                    {/* Data Items Grid */}
                    <div className="flex-1 p-6 overflow-y-auto">
                        {loading ? (
                            <div className="text-center py-16">
                                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                                    <FaDatabase className="text-4xl text-gray-300" />
                                </div>
                                <p>Loading...</p>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                                    <FaDatabase className="text-4xl text-gray-300" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                                    {(activeTab === 'castes' || activeTab === 'subcastes') && !selectedParent
                                        ? `Select a ${activeTab === 'castes' ? 'religion' : 'caste'} to view items`
                                        : `No ${activeTab} found`}
                                </h3>
                                <p className="text-gray-600">
                                    {searchTerm ? 'Try a different search term' : `Add your first ${activeTab.slice(0, -1)} item`}
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredData.map((item, index) => (
                                    <div 
                                        key={index} 
                                        className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 group"
                                    >
                                        {editingItem.category === activeTab && editingItem.index === index ? (
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-sm font-bold text-gray-400 uppercase">Edit Item</h4>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={saveEdit}
                                                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingItem({ category: null, index: null, value: '' })}
                                                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    value={editingItem.value}
                                                    onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    autoFocus
                                                    onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="font-medium text-gray-800 text-lg">{item}</span>
                                                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={() => startEditing(item, index)}
                                                            className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200"
                                                            title="Edit"
                                                        >
                                                            <FaEdit className="text-sm" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item)}
                                                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200"
                                                            title="Delete"
                                                        >
                                                            <FaTrash className="text-sm" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center text-sm text-gray-500">
                                                    <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                                        ID: {index + 1}
                                                    </span>
                                                    <span className="ml-2">
                                                        {new Date().toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer Stats */}
                    <div className="p-6 border-t border-gray-200 bg-gray-50">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="flex items-center space-x-6">
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">Showing</p>
                                    <p className="text-lg font-bold text-gray-800">{filteredData.length} items</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">Total</p>
                                    <p className="text-lg font-bold text-gray-800">{currentItems.length} items</p>
                                </div>
                            </div>
                            <div className="mt-4 md:mt-0">
                                <button 
                                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center"
                                    onClick={() => {
                                        const exportData = currentItems.join('\n');
                                        const blob = new Blob([exportData], { type: 'text/plain' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `${activeTab}${selectedParent ? `_${selectedParent}` : ''}_export.txt`;
                                        a.click();
                                        openModal('success', 'Exported', `${activeTab} exported successfully`);
                                    }}
                                >
                                    <FaSave className="mr-2" /> Export {activeTab}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MasterDataManagement;